/**
 * CoPilotConversation — the shared Companion chat body (SR2 parity).
 *
 * This is the ONE implementation of the Companion conversation UI, rendered by
 * BOTH portals (journey-web / business-web) so the two can never drift. It was
 * extracted verbatim from journey-web's `CoPilotBrain` and made **fully
 * controlled**: it fetches nothing and owns no server state — the host passes
 * the `useCoPilotBrainCore` return value straight through.
 *
 * Everything that used to be portal-specific is an injected prop:
 *  - `decorateStrong` — brand decoration for **bold** runs (journey passes its
 *    hero decorator; business passes nothing).
 *  - `billingHref` / `careHref` — each portal owns its billing and Care routes.
 *  - `proactiveSlot` / `avatar` — optional per-portal chrome.
 *
 * The safety banner is the engine's (`safetyBanner`, the latest turn's as the
 * server built it): a critical one offers the crisis lines (D2).
 *
 * When the AI service is not answering (`aiUnavailable` — a turn failed as
 * unavailable and the brain's health confirms it is down), the panel shows ONE
 * calm notice in place of that turn's error: some features are temporarily
 * unavailable, we keep trying, the rest of Tonnie works — with "Try again"
 * (Journey PRD § Brain API Failures).
 *
 * The per-message action row holds the feedback thumbs and the Shorter/Longer
 * rewrite actions ONLY. A per-reply "Tonnie-Checked" badge used to sit there
 * too; it repeated on every bubble, said nothing a user acts on, and crowded
 * the controls that do — the badge belongs on artefacts (exports, credentials),
 * not on conversation turns.
 */

import { useTranslation } from '@tonnie/locale';
import { AiUnavailableNotice, Button, cn, Markdown } from '@tonnie/ui';
import { History, Loader2, Sparkles, SquarePen, ThumbsDown, ThumbsUp } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  parseChoices,
  type CoPilotMessage,
  type CoPilotToolActivity,
} from '@tonnie/copilot/client';
import type { CoPilotActionProposal, SafetyBannerConfig } from '@tonnie/types';

import { ChoiceChips } from './ChoiceChips';
import { CoPilotBrainInput } from './CoPilotBrainInput';
import { CoPilotHistory } from './CoPilotHistory';
import { EnvelopeRenderer } from './EnvelopeRenderer';
import { ProposalCard } from './ProposalCard';
import { SafetyBanner } from './SafetyBanner';
import { RESPONSE_DEPTHS, type ResponseDepth } from '@tonnie/copilot-core';

export interface ICoPilotConversationProps {
  // ── conversation state (from useCoPilotBrainCore) ──────────────────────
  messages: CoPilotMessage[];
  input: string;
  setInput: (value: string) => void;
  handleSend: () => void;
  sendMessage: (text: string) => void;
  handleFeedback: (messageId: string, feedback: 'up' | 'down') => void;
  handleClearConversation: () => void;
  // ── conversation history (0089) ────────────────────────────────────────
  /** The open conversation, or null for a brand-new unsent chat. */
  currentConversationId?: string | null;
  /** Open a past conversation and continue it. */
  selectConversation?: (id: string) => void;
  /** Start a fresh chat (client-only; no server row until the first message). */
  startNewConversation?: () => void;
  responseDepth: ResponseDepth;
  setResponseDepth: (depth: ResponseDepth) => void;
  /** "Search the web" composer toggle state (BL-213 v2). */
  webSearchEnabled?: boolean;
  setWebSearchEnabled?: (on: boolean) => void;
  proposals: CoPilotActionProposal[];
  handleConfirmProposal: (
    proposalId: string,
    typedToken?: string
  ) => Promise<'success' | 'stale' | 'error'>;
  handleDismissProposal: (proposalId: string) => void;
  handleUpload: (file: File, kind: 'audio' | 'image' | 'document') => void;
  uploadsEnabled: boolean;
  pendingAttachment: { fileName: string } | null;
  clearPendingAttachment: () => void;
  handleSuggestionClick: (suggestion: string) => void;
  isLoading: boolean;
  errorKey: string | null;
  errorTraceId?: string | null;
  errorDetail?: string | null;
  degraded: boolean;
  /** The AI service is down (the engine's): the calm notice replaces the turn's error. */
  aiUnavailable: boolean;
  /** Ask the AI service again now. */
  recheckAi: () => void;
  recheckingAi: boolean;
  quota?: {
    used?: number;
    limit?: number;
    remaining?: number;
    unlimited?: boolean;
    warn?: boolean;
  };
  /** The tool running right now (web search, etc.) — drives the live progress row. */
  toolActivity?: CoPilotToolActivity | null;

  // ── per-portal configuration ───────────────────────────────────────────
  /** First-run suggested questions. */
  suggestions?: string[];
  /** Brand decoration for **bold** runs. Omit for no decoration. */
  decorateStrong?: (text: string) => ReactNode | null;
  /** Billing route for the upgrade CTAs. */
  billingHref?: string;
  /**
   * The safety banner above the toolbar — the engine's: the latest turn's, as
   * the server built it, or the standard one.
   */
  safetyBanner: SafetyBannerConfig;
  /** Care support route a critical banner's help links to; omitted where the portal has none. */
  careHref?: string;
  /** Rendered under the toolbar (e.g. the proactive corner-cue). */
  proactiveSlot?: ReactNode;
}

export function CoPilotConversation({
  messages,
  input,
  setInput,
  handleSend,
  sendMessage,
  handleFeedback,
  handleClearConversation,
  currentConversationId = null,
  selectConversation,
  startNewConversation,
  responseDepth,
  setResponseDepth,
  webSearchEnabled,
  setWebSearchEnabled,
  proposals,
  handleConfirmProposal,
  handleDismissProposal,
  handleUpload,
  uploadsEnabled,
  pendingAttachment,
  clearPendingAttachment,
  handleSuggestionClick,
  isLoading,
  errorKey,
  errorTraceId,
  errorDetail,
  degraded,
  aiUnavailable,
  recheckAi,
  recheckingAi,
  quota,
  toolActivity,
  suggestions = [],
  decorateStrong,
  billingHref,
  safetyBanner,
  careHref,
  proactiveSlot,
}: ICoPilotConversationProps) {
  const { t } = useTranslation();

  // Literal keys, one row per depth: the compiler holds the rows to the
  // vocabulary and the key-usage gate checks every key against en.json.
  const depthCopy: Record<ResponseDepth, { label: string; hint: string }> = {
    brief: { label: t('copilot.depth.brief'), hint: t('copilot.depth.briefHint') },
    balanced: { label: t('copilot.depth.balanced'), hint: t('copilot.depth.balancedHint') },
    detailed: { label: t('copilot.depth.detailed'), hint: t('copilot.depth.detailedHint') },
  };

  // In-panel view swap (0089): the same 384px panel flips between the live chat
  // and the ChatGPT-style conversation-history list. History is only available
  // when the host wired the engine actions (both web portals do).
  const historyEnabled = !!selectConversation && !!startNewConversation;
  const [view, setView] = useState<'chat' | 'history'>('chat');

  const remaining = quota?.remaining ?? 0;
  const isQuotaExceeded = quota ? !quota.unlimited && remaining <= 0 : false;

  // The newest real assistant reply — only it shows the per-reply "Shorter /
  // Longer" rewrite actions (Gemini pattern); rewriting always targets the last.
  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (
        m.role === 'assistant' &&
        !m.streaming &&
        m.id !== 'greeting' &&
        !m.id.startsWith('notice-')
      ) {
        return m.id;
      }
    }
    return null;
  }, [messages]);

  // Keep the newest content in view: auto-scroll to the bottom as messages
  // stream in and when an action's confirmation card appears — otherwise the
  // reply (and the tap-to-Confirm ProposalCard) render below the fold.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // INSTANT scroll (not 'smooth') — a smooth animation restarts on every
    // streamed token and reads as flicker/jank. And only follow when the user
    // is already near the bottom, so scrolling up to re-read isn't yanked down.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages, proposals, isLoading]);

  // History view — replaces the chat body in the same panel (in-panel swap).
  if (view === 'history' && historyEnabled) {
    return (
      <CoPilotHistory
        currentConversationId={currentConversationId}
        onSelect={(id) => {
          selectConversation?.(id);
          setView('chat');
        }}
        onNewConversation={() => {
          startNewConversation?.();
          setView('chat');
        }}
        onBack={() => setView('chat')}
      />
    );
  }

  return (
    <>
      <SafetyBanner config={safetyBanner} careHref={careHref} />

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Toolbar: answer-length control (left) + New conversation (right).
            Depth is persisted and applied to every turn (Claude "Styles" /
            Copilot "conversation style" pattern). New conversation clears the
            thread + server session so the Companion starts fresh. */}
        <div className="flex items-center justify-between gap-2 px-4 pt-2">
          <div
            className="flex items-center gap-0.5 rounded-full bg-copilot-bubble p-0.5"
            role="group"
            aria-label={t('copilot.depth.label')}
          >
            {RESPONSE_DEPTHS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setResponseDepth(d)}
                aria-pressed={responseDepth === d}
                title={depthCopy[d].hint}
                data-testid={`copilot-depth-${d}`}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  responseDepth === d
                    ? 'bg-copilot-surface font-medium text-copilot-foreground shadow-sm'
                    : 'text-copilot-muted hover:text-copilot-foreground'
                )}
              >
                {depthCopy[d].label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {historyEnabled && (
              <button
                type="button"
                onClick={() => setView('history')}
                data-testid="copilot-history-button"
                aria-label={t('copilot.history.title')}
                title={t('copilot.history.title')}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-copilot-muted',
                  'transition-colors hover:bg-copilot-bubble hover:text-copilot-foreground'
                )}
              >
                <History className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
            {messages.length > 1 && (
              <button
                type="button"
                onClick={handleClearConversation}
                disabled={isLoading}
                data-testid="copilot-new-conversation"
                aria-label={t('copilot.newConversation')}
                title={t('copilot.newConversation')}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-copilot-muted',
                  'transition-colors hover:bg-copilot-bubble hover:text-copilot-foreground disabled:opacity-50'
                )}
              >
                <SquarePen className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {proactiveSlot}

        {suggestions.length > 0 && messages.length <= 1 && (
          <div className="px-4 pt-4 pb-2">
            <p className="text-sm text-muted-foreground mb-2">{t('copilot.suggestedQuestions')}</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-xs"
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              // Per-message anchors (parity with citizen-mobile's CoPilotConversation).
              // `data-streaming` flips to "false" when the turn's `end` event lands,
              // which is the ONLY reliable "this reply is complete" signal — the
              // blinking cursor and feedback row are styling/i18n details. E2E reads
              // the finished reply from the newest `copilot-message-bot-*` whose
              // data-streaming is "false"; without these, capturing a streamed answer
              // means scraping DOM position and races the token stream.
              data-testid={
                msg.role === 'user'
                  ? `copilot-message-user-${msg.id}`
                  : `copilot-message-bot-${msg.id}`
              }
              data-streaming={msg.streaming ? 'true' : 'false'}
              className={cn('max-w-[85%]', msg.role === 'user' ? 'ml-auto' : 'mr-auto')}
            >
              {msg.role === 'user' ? (
                <div className="bg-copilot-user text-copilot-accent-foreground rounded-2xl rounded-br-sm px-3 py-2 text-sm">
                  {msg.text}
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-copilot-accent/10 text-copilot-accent"
                    aria-hidden="true"
                    data-testid="copilot-avatar"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1 bg-copilot-bubble text-copilot-foreground rounded-2xl rounded-bl-sm px-3 py-2 space-y-2">
                    {msg.text &&
                      (msg.streaming ? (
                        // PROGRESSIVE markdown: format as it streams (like
                        // ChatGPT/Claude). Choice chips are held back until the
                        // reply completes — and the trailing `tonnie-choices`
                        // fence (appended on the `final` frame, which lands one
                        // tick before `end`) is stripped here too, so it never
                        // flashes as a raw code block in that window.
                        <Markdown decorateStrong={decorateStrong} isStreaming>
                          {parseChoices(msg.text).text}
                        </Markdown>
                      ) : (
                        (() => {
                          // Done streaming: split any trailing choices block into
                          // clickable chips + render the full markdown.
                          const { text, options } = parseChoices(msg.text);
                          return (
                            <>
                              {text && <Markdown decorateStrong={decorateStrong}>{text}</Markdown>}
                              {options.length > 0 && (
                                <ChoiceChips
                                  options={options}
                                  onPick={sendMessage}
                                  disabled={isLoading}
                                />
                              )}
                            </>
                          );
                        })()
                      ))}
                    {msg.envelopes.map((env, i) => (
                      <EnvelopeRenderer key={i} envelope={env} />
                    ))}
                    {msg.streaming && (
                      <span className="inline-block w-2 h-4 bg-copilot-muted/50 animate-pulse align-middle" />
                    )}
                    {!msg.streaming && msg.id !== 'greeting' && !msg.id.startsWith('notice-') && (
                      <div className="flex items-center gap-1 pt-1.5 -mb-0.5">
                        <button
                          type="button"
                          aria-label={t('copilot.feedback.helpful')}
                          aria-pressed={msg.feedback === 'up'}
                          onClick={() => handleFeedback(msg.id, 'up')}
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                            'text-copilot-muted hover:bg-copilot-accent/10 hover:text-copilot-accent',
                            msg.feedback === 'up' && 'bg-copilot-accent/15 text-copilot-accent'
                          )}
                        >
                          <ThumbsUp
                            className="h-3.5 w-3.5"
                            fill={msg.feedback === 'up' ? 'currentColor' : 'none'}
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          type="button"
                          aria-label={t('copilot.feedback.notHelpful')}
                          aria-pressed={msg.feedback === 'down'}
                          onClick={() => handleFeedback(msg.id, 'down')}
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                            'text-muted-foreground hover:bg-destructive/10 hover:text-destructive-accessible',
                            msg.feedback === 'down' &&
                              'bg-destructive/15 text-destructive-accessible'
                          )}
                        >
                          <ThumbsDown
                            className="h-3.5 w-3.5"
                            fill={msg.feedback === 'down' ? 'currentColor' : 'none'}
                            aria-hidden="true"
                          />
                        </button>
                        {msg.feedback && (
                          <span className="ml-1 text-2xs text-muted-foreground">
                            {t('copilot.feedback.thanks')}
                          </span>
                        )}
                        {/* Per-reply rewrite (Gemini pattern) — only on the
                            newest answer; re-asks the model to redo it. */}
                        {msg.id === lastAssistantId && !isLoading && (
                          <div className="ml-auto flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => sendMessage(t('copilot.rewrite.shorterCommand'))}
                              className="rounded-full px-2 py-1 text-2xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                            >
                              {t('copilot.rewrite.shorter')}
                            </button>
                            <button
                              type="button"
                              onClick={() => sendMessage(t('copilot.rewrite.longerCommand'))}
                              className="rounded-full px-2 py-1 text-2xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                            >
                              {t('copilot.rewrite.longer')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {isLoading && !messages.some((m) => m.streaming) && (
            <div className="mr-auto max-w-[85%]">
              <div className="flex items-start gap-2">
                <span
                  className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-copilot-accent/10 text-copilot-accent"
                  aria-hidden="true"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
                {/* While a tool runs (e.g. web search) show its live label with a
                    spinner so the pre-prose wait reads as progress, not a hang.
                    Falls back to the generic "thinking" pulse otherwise. */}
                {toolActivity ? (
                  <div
                    className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-copilot-bubble px-3 py-2 text-sm text-copilot-foreground"
                    data-testid="copilot-tool-activity"
                    aria-live="polite"
                  >
                    <Loader2
                      className="h-3.5 w-3.5 shrink-0 animate-spin text-copilot-accent"
                      aria-hidden="true"
                    />
                    <span>{t(toolActivity.labelKey)}</span>
                  </div>
                ) : (
                  // The spinner says "working"; the words stay at full contrast
                  // (a pulse faded them to 50% half the time).
                  <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-copilot-bubble px-3 py-2 text-sm text-copilot-muted">
                    <Loader2
                      className="h-3.5 w-3.5 shrink-0 animate-spin text-copilot-accent"
                      aria-hidden="true"
                    />
                    <span>{t('copilot.thinking')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {aiUnavailable ? (
            <AiUnavailableNotice
              size="inline"
              description={t('copilot.aiUnavailable.description')}
              onRetry={recheckAi}
              retrying={recheckingAi}
              data-testid="copilot-ai-unavailable"
            />
          ) : errorKey ? (
            <div className="mr-auto max-w-[85%]">
              <div className="bg-destructive/10 text-destructive-accessible rounded-2xl px-3 py-2 text-sm">
                {t(errorKey)}
                {errorTraceId && (
                  <span className="mt-1 block text-2xs">
                    {t('copilot.errors.ref')}: {errorTraceId}
                  </span>
                )}
                {/* DEV-only raw cause (backend omits it in prod). */}
                {errorDetail && (
                  <span className="mt-1 block font-mono text-2xs">{errorDetail}</span>
                )}
              </div>
            </div>
          ) : null}

          {proposals.length > 0 && (
            <div className="space-y-2">
              {proposals.map((proposal) => (
                <ProposalCard
                  key={proposal.proposalId}
                  proposal={proposal}
                  onConfirm={handleConfirmProposal}
                  onDismiss={handleDismissProposal}
                />
              ))}
            </div>
          )}
        </div>

        {degraded && (
          <div className="px-4 py-1 text-2xs text-muted-foreground border-t border-sidebar-border">
            {t('copilot.errors.budget_degraded')}
          </div>
        )}

        <CoPilotBrainInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          onUpload={handleUpload}
          uploadsEnabled={uploadsEnabled}
          isLoading={isLoading}
          attachmentName={pendingAttachment?.fileName ?? null}
          onRemoveAttachment={clearPendingAttachment}
          quotaUsed={quota?.used ?? 0}
          quotaLimit={quota?.unlimited ? 'unlimited' : (quota?.limit ?? 0)}
          isQuotaExceeded={isQuotaExceeded}
          webSearchEnabled={webSearchEnabled}
          onToggleWebSearch={setWebSearchEnabled}
          billingHref={billingHref}
        />

        {quota && !quota.unlimited && (
          <div className="px-4 py-1.5 text-2xs text-muted-foreground border-t border-sidebar-border flex items-center justify-between">
            <span className={cn(quota.warn && 'text-destructive-accessible')}>
              {t('copilot.quota.remaining')}: {remaining}
            </span>
            {remaining <= 0 && (
              <a
                href={billingHref ?? '/billing'}
                className="text-primary-accessible underline hover:no-underline"
              >
                {t('copilot.quota.upgrade')}
              </a>
            )}
          </div>
        )}
      </div>
    </>
  );
}
